/**
 * PLP collection guide — normalize Manus / loose HTML to match Bar Stools layout:
 * section heading (full width) → 2-column body, dividers, first-section preview + Read more.
 */
(function () {
  const SECTION_MIN = 40;
  const MIN_PREVIEW_CHARS = 20;

  const cleanText = (value) => (value || '').replace(/\s+/g, ' ').trim();

  const TITLE_MAX_WORDS = 18;

  const isHeadingText = (text) => {
    const t = cleanText(text);
    if (!t) return false;
    const words = t.split(/\s+/);
    // Question titles must END with "?" and stay short — mid-sentence "?" in body copy is not a title
    if (/\?$/.test(t) && words.length <= TITLE_MAX_WORDS) return true;
    if (/[.!:]$/.test(t)) return false;
    return t.length >= SECTION_MIN && words.length >= 8 && words.length <= TITLE_MAX_WORDS;
  };

  const isShortLabel = (text) => {
    const t = cleanText(text);
    if (!t || /\?$/.test(t)) return false;
    const words = t.split(/\s+/);
    return t.length < SECTION_MIN && words.length <= 6 && !/[.!?]$/.test(t);
  };

  const getTitleText = (el) => {
    if (!el || el.tagName !== 'P') return null;
    const full = cleanText(el.textContent);
    if (!full) return null;
    if (/^explore:?\s*/i.test(full)) return null;

    const clone = el.cloneNode(true);
    clone.querySelectorAll('a').forEach((a) => a.replaceWith(...a.childNodes));
    clone.querySelectorAll('strong, b, em, u').forEach((n) => n.replaceWith(...n.childNodes));
    const residual = cleanText(clone.textContent);
    const hasEmph = Boolean(el.querySelector('strong, b'));
    const hasOnlyEmphOrLink =
      hasEmph &&
      residual === full &&
      !el.querySelector('img') &&
      Array.from(el.childNodes).every((n) => {
        if (n.nodeType === Node.TEXT_NODE) return !cleanText(n.textContent);
        if (n.nodeType !== Node.ELEMENT_NODE) return true;
        return ['STRONG', 'B', 'EM', 'U', 'A', 'SPAN'].includes(n.tagName);
      });

    if (hasOnlyEmphOrLink && (isHeadingText(full) || isShortLabel(full))) return full;
    if (!hasEmph && !el.querySelector('a') && (isHeadingText(full) || isShortLabel(full))) return full;
    return null;
  };

  const normalizeExplore = (p) => {
    const full = cleanText(p.textContent);
    if (!/^explore:?\s*/i.test(full)) return p;

    const existing = p.querySelector('a[href]');
    const next = document.createElement('p');
    const a = document.createElement('a');
    if (existing) {
      a.href = existing.getAttribute('href');
      let label = cleanText(existing.textContent) || full.replace(/^explore:?\s*/i, '');
      if (!/^explore\b/i.test(label)) label = `Explore ${label}`;
      a.textContent = label;
    } else {
      const label = full.replace(/^explore:?\s*/i, '');
      a.href = `/collections/${label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')}`;
      a.textContent = `Explore ${label}`;
    }
    next.appendChild(a);
    return next;
  };

  const normalizeTable = (table) => {
    const clone = table.cloneNode(true);
    const firstRow = clone.querySelector('tr');
    if (!firstRow) return clone;

    const cells = Array.from(firstRow.children);
    const looksLikeHeader = cells.every((cell) => {
      const t = cleanText(cell.textContent);
      return t && (cell.querySelector('strong, b') || t.length < 40);
    });

    if (looksLikeHeader && !clone.querySelector('thead')) {
      const thead = document.createElement('thead');
      const row = document.createElement('tr');
      cells.forEach((cell) => {
        const th = document.createElement('th');
        th.textContent = cleanText(cell.textContent);
        row.appendChild(th);
      });
      thead.appendChild(row);
      firstRow.remove();
      let tbody = clone.querySelector('tbody');
      if (!tbody) {
        tbody = document.createElement('tbody');
        Array.from(clone.querySelectorAll('tr')).forEach((tr) => tbody.appendChild(tr));
        clone.appendChild(tbody);
      }
      clone.insertBefore(thead, tbody);
    }

    clone.querySelectorAll('th, td').forEach((cell) => {
      const text = cleanText(cell.textContent);
      cell.innerHTML = '';
      cell.textContent = text;
    });
    return clone;
  };

  const mergeLoneLists = (nodes) => {
    const out = [];
    let pending = null;

    const flushList = () => {
      if (pending && pending.children.length) out.push(pending);
      pending = null;
    };

    nodes.forEach((node) => {
      if (node.tagName === 'UL' || node.tagName === 'OL') {
        const items = Array.from(node.children).filter((c) => c.tagName === 'LI');
        if (items.length === 1) {
          if (!pending || pending.tagName !== node.tagName) {
            flushList();
            pending = document.createElement(node.tagName);
          }
          if (cleanText(items[0].textContent)) {
            pending.appendChild(items[0].cloneNode(true));
          }
          return;
        }
        flushList();
        const cleaned = node.cloneNode(false);
        items.forEach((li) => {
          if (cleanText(li.textContent)) cleaned.appendChild(li.cloneNode(true));
        });
        if (cleaned.children.length) out.push(cleaned);
        return;
      }
      flushList();
      out.push(node);
    });
    flushList();
    return out;
  };

  const flattenNodes = (root) => {
    const out = [];
    const walk = (node) => {
      if (!node) return;
      if (node.nodeType === Node.TEXT_NODE) {
        const t = cleanText(node.textContent);
        if (t) {
          const p = document.createElement('p');
          p.textContent = t;
          out.push(p);
        }
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const tag = node.tagName;
      if (tag === 'SCRIPT' || tag === 'STYLE') return;

      if (
        tag === 'DIV' &&
        (node.classList.contains('sec-CollectionGuide_Block') ||
          node.classList.contains('sec-CollectionGuide_Preview') ||
          node.classList.contains('sec-CollectionGuide_Preview--mobile') ||
          node.classList.contains('sec-CollectionGuide_Preview--desktop') ||
          node.classList.contains('sec-CollectionGuide_State'))
      ) {
        Array.from(node.childNodes).forEach(walk);
        return;
      }
      if (tag === 'DIV') {
        Array.from(node.childNodes).forEach(walk);
        return;
      }
      if (tag === 'H2' || tag === 'H3' || tag === 'H4') {
        const text = cleanText(node.textContent);
        if (!text) return;
        const p = document.createElement('p');
        p.className = 'sec-CollectionGuide_Question';
        const strong = document.createElement('strong');
        strong.textContent = text;
        p.appendChild(strong);
        out.push(p);
        return;
      }
      if (tag === 'TABLE') {
        out.push(normalizeTable(node));
        return;
      }
      if (tag === 'UL' || tag === 'OL') {
        out.push(node.cloneNode(true));
        return;
      }
      if (tag === 'P' || tag === 'BLOCKQUOTE') {
        const cloned = normalizeExplore(node.cloneNode(true));
        if (cleanText(cloned.textContent)) out.push(cloned);
        return;
      }
      Array.from(node.childNodes).forEach(walk);
    };
    Array.from(root.childNodes).forEach(walk);
    return mergeLoneLists(out);
  };

  const buildBlocks = (nodes) => {
    const blocks = [];
    let current = [];

    const flush = () => {
      if (!current.length) return;
      const block = document.createElement('div');
      block.className = 'sec-CollectionGuide_Block';
      current.forEach((n) => block.appendChild(n));
      if (cleanText(block.textContent).length >= MIN_PREVIEW_CHARS) {
        blocks.push(block);
      }
      current = [];
    };

    nodes.forEach((node) => {
      if (node.tagName === 'P') {
        const title = getTitleText(node);
        if (title && isHeadingText(title)) {
          flush();
          const q = document.createElement('p');
          q.className = 'sec-CollectionGuide_Question';
          const strong = document.createElement('strong');
          strong.textContent = title;
          q.appendChild(strong);
          current.push(q);
          return;
        }
        if (title && isShortLabel(title)) {
          const label = document.createElement('p');
          const strong = document.createElement('strong');
          strong.textContent = title;
          label.appendChild(strong);
          current.push(label);
          return;
        }
      }
      current.push(node);
    });
    flush();
    return blocks;
  };

  const cloneBlock = (block) => block.cloneNode(true);

  const countWords = (text) =>
    cleanText(text)
      .split(/\s+/)
      .filter(Boolean).length;

  /**
   * Canonical source is always the expanded (full) columns.
   * Collapsed may contain dual mobile+desktop previews of the same copy — using that
   * as source duplicated sections after Read more.
   */
  const pickSourceColumns = (collapsed, expanded) => {
    const expandedCols = expanded?.querySelector('.sec-CollectionGuide_Columns');
    if (expandedCols && cleanText(expandedCols.textContent)) return expandedCols;
    return collapsed?.querySelector('.sec-CollectionGuide_Columns') || null;
  };

  const truncateBlockToWords = (block, limit) => {
    const clone = cloneBlock(block);
    if (!Number.isFinite(limit) || limit < 1) return clone;

    const children = Array.from(clone.children);
    if (!children.length) {
      const words = cleanText(clone.textContent).split(/\s+/).filter(Boolean);
      if (words.length > limit) {
        clone.textContent = `${words.slice(0, limit).join(' ')}…`;
      }
      return clone;
    }

    let wordsUsed = 0;
    let done = false;
    children.forEach((child, index) => {
      if (done) {
        child.remove();
        return;
      }

      const isQuestion =
        child.classList?.contains('sec-CollectionGuide_Question') ||
        (index === 0 && child.querySelector?.('strong') && child.tagName === 'P');

      const childWords = countWords(child.textContent);

      if (isQuestion) {
        wordsUsed += childWords;
        return;
      }

      const remaining = limit - wordsUsed;
      if (remaining <= 0) {
        child.remove();
        done = true;
        return;
      }

      if (childWords > remaining) {
        const words = cleanText(child.textContent).split(/\s+/).filter(Boolean);
        child.textContent = `${words.slice(0, remaining).join(' ')}…`;
        wordsUsed = limit;
        done = true;
        return;
      }

      wordsUsed += childWords;
    });

    return clone;
  };

  const bindReadMore = (body) => {
    if (!body || body.dataset.guideReadMoreBound === '1') return;
    const collapsed = body.querySelector('[data-el="un-truncate.content"]');
    const expanded = body.querySelector('[data-el="un-truncate.fullContent"]');
    if (!collapsed || !expanded) return;

    body.dataset.guideReadMoreBound = '1';
    body.addEventListener('click', (event) => {
      const openBtn = event.target.closest('[data-el="un-truncate.triggerOpen"]');
      const closeBtn = event.target.closest('[data-el="un-truncate.triggerClose"]');
      if (!openBtn && !closeBtn) return;
      if (!body.contains(openBtn || closeBtn)) return;

      event.preventDefault();
      if (openBtn) {
        body.classList.add('is-expanded');
        collapsed.style.removeProperty('display');
        expanded.style.removeProperty('display');
        return;
      }

      body.classList.remove('is-expanded');
      collapsed.style.removeProperty('display');
      expanded.style.removeProperty('display');

      const guide = body.closest('.sec-CollectionGuide') || body;
      const header = document.querySelector('sticky-header');
      const headerOffset = header ? Math.ceil(header.getBoundingClientRect().height) : 0;
      const top = guide.getBoundingClientRect().top + window.pageYOffset - headerOffset - 16;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    });
  };

  const ensureActions = (stateEl, isOpen, openLabel, closeLabel) => {
    let actions = stateEl.querySelector('.sec-CollectionGuide_Actions');
    const label = isOpen ? closeLabel : openLabel;
    if (actions) {
      const btn = actions.querySelector('button');
      if (btn) {
        btn.setAttribute('data-el', isOpen ? 'un-truncate.triggerClose' : 'un-truncate.triggerOpen');
        btn.textContent = label;
      }
      return actions;
    }
    actions = document.createElement('div');
    actions.className = 'sec-CollectionGuide_Actions';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'button button--primary sec-CollectionGuide_ReadMore';
    btn.setAttribute('data-el', isOpen ? 'un-truncate.triggerClose' : 'un-truncate.triggerOpen');
    btn.textContent = label;
    actions.appendChild(btn);
    stateEl.appendChild(actions);
    return actions;
  };

  const normalizeGuide = (section) => {
    const body = section.querySelector('.sec-CollectionGuide_Body');
    if (!body || body.dataset.guideNormalized === '1') return;

    let collapsed = body.querySelector('[data-el="un-truncate.content"]');
    let expanded = body.querySelector('[data-el="un-truncate.fullContent"]');
    const sourceCols = pickSourceColumns(collapsed, expanded);
    if (!sourceCols) return;

    const openBtn = body.querySelector('[data-el="un-truncate.triggerOpen"]');
    const closeBtn = body.querySelector('[data-el="un-truncate.triggerClose"]');
    const openLabel = cleanText(openBtn?.textContent) || 'Read more';
    const closeLabel = cleanText(closeBtn?.textContent) || 'Read less';
    const wordLimit = Number.parseInt(body.getAttribute('data-guide-word-limit') || '130', 10);
    const liquidWantsReadMore = body.getAttribute('data-guide-read-more') === 'true';

    const source = sourceCols.cloneNode(true);
    /* Dual preview shells can both hold the same HTML — only walk one if present. */
    const dualDesktop = source.querySelector('.sec-CollectionGuide_Preview--desktop');
    const dualMobile = source.querySelector('.sec-CollectionGuide_Preview--mobile');
    if (dualDesktop && dualMobile) {
      dualMobile.remove();
    }

    const nodes = flattenNodes(source);
    if (!nodes.length) {
      bindReadMore(body);
      return;
    }

    const blocks = buildBlocks(nodes);
    if (!blocks.length) {
      bindReadMore(body);
      return;
    }

    body.classList.add('sec-CollectionGuide_Body--split');
    body.dataset.guideNormalized = '1';

    const previewBlock =
      blocks.find((block) => cleanText(block.textContent).length >= MIN_PREVIEW_CHARS) || blocks[0];
    const totalWords = countWords(blocks.map((block) => block.textContent).join(' '));
    const needsReadMore =
      liquidWantsReadMore || blocks.length > 1 || totalWords > wordLimit;

    if (!collapsed) {
      collapsed = document.createElement('div');
      collapsed.setAttribute('data-el', 'un-truncate.content');
      collapsed.className = 'sec-CollectionGuide_State';
      const cols = document.createElement('div');
      cols.className = 'sec-CollectionGuide_Columns body_large regular';
      collapsed.appendChild(cols);
      body.insertBefore(collapsed, body.firstChild);
    }

    if (!expanded) {
      expanded = document.createElement('div');
      expanded.setAttribute('data-el', 'un-truncate.fullContent');
      expanded.className = 'sec-CollectionGuide_State clc-Hero_Text-hidden';
      const cols = document.createElement('div');
      cols.className = 'sec-CollectionGuide_Columns body_large regular';
      expanded.appendChild(cols);
      body.appendChild(expanded);
    }

    const expandedCols = expanded.querySelector('.sec-CollectionGuide_Columns');
    const collapsedCols = collapsed.querySelector('.sec-CollectionGuide_Columns');
    if (!expandedCols || !collapsedCols) return;

    expandedCols.className = 'sec-CollectionGuide_Columns body_large regular sec-CollectionGuide_Columns--split';
    collapsedCols.className = 'sec-CollectionGuide_Columns body_large regular sec-CollectionGuide_Columns--split';

    expandedCols.replaceChildren(...blocks.map((block) => cloneBlock(block)));

    if (needsReadMore) {
      if (blocks.length > 1) {
        collapsedCols.replaceChildren(cloneBlock(previewBlock));
      } else {
        collapsedCols.replaceChildren(truncateBlockToWords(previewBlock, wordLimit));
      }

      /* Safety: never leave an empty collapsed shell (legacy empty <p></p> preview). */
      if (cleanText(collapsedCols.textContent).length < MIN_PREVIEW_CHARS) {
        collapsedCols.replaceChildren(...blocks.map((block) => cloneBlock(block)));
        collapsed.querySelectorAll('.sec-CollectionGuide_Actions').forEach((el) => el.remove());
        expanded.remove();
        body.dataset.guideV = '2';
        return;
      }

      /* If preview still matches full text, word-truncate so expand is meaningful. */
      if (cleanText(collapsedCols.textContent) === cleanText(expandedCols.textContent)) {
        collapsedCols.replaceChildren(truncateBlockToWords(previewBlock, wordLimit));
      }

      if (cleanText(collapsedCols.textContent) === cleanText(expandedCols.textContent)) {
        collapsed.querySelectorAll('.sec-CollectionGuide_Actions').forEach((el) => el.remove());
        expanded.remove();
        body.dataset.guideV = '2';
        return;
      }

      ensureActions(collapsed, false, openLabel, closeLabel);
      ensureActions(expanded, true, openLabel, closeLabel);
      expanded.classList.add('clc-Hero_Text-hidden');
      body.dataset.guideV = '2';
      bindReadMore(body);
    } else {
      collapsedCols.replaceChildren(...blocks.map((block) => cloneBlock(block)));
      collapsed.querySelectorAll('.sec-CollectionGuide_Actions').forEach((el) => el.remove());
      expanded.remove();
      body.dataset.guideV = '2';
    }
  };

  const run = () => {
    document.querySelectorAll('.sec-CollectionGuide').forEach((section) => {
      const body = section.querySelector('.sec-CollectionGuide_Body');
      if (body) {
        body.removeAttribute('data-guide-normalized');
        body.removeAttribute('data-guide-read-more-bound');
      }
      normalizeGuide(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  document.addEventListener('shopify:section:load', run);
})();
